import React, { useEffect } from "react";
import { Grid, Type, Flex } from "mesh-component-library";
import { determineLocale, useLocale } from "../../../../contexts/LocaleContext";
import { TRANSLATION_CONSTANTS } from "../../../../app-consts/translations";
import StyledSavingsOffer from "./SavingsOffer.styles";
import GoogleWallet from "../../../../assets/google-wallet.svg";
import GoogleWalletSpanish from "../../../../assets/google-wallet-es.svg";
import GoogleWalletIOS from "../../../../assets/google-wallet-ios.svg";
import GoogleWalletIOSSpanish from "../../../../assets/google-wallet-ios-es.svg";
import { es_US, JUMP_START, TIRE_CHANGE, TOW } from "../../../../app-consts/appConstants";
import { CreateCouponForPepRequestPayload } from "../../actions";
import { CouponType, DeviceType } from "../../types";
import { datadogRum } from "@datadog/browser-rum";

export type SavingsOfferProps = {
  callId: string;
  couponType: CouponType;
  couponPrecursor: string;
  deviceType?: DeviceType;
  displayCode: string;
  onClick: (data: CreateCouponForPepRequestPayload) => void;
};

export const SavingsOffer = ({
  callId,
  couponPrecursor,
  couponType,
  deviceType,
  displayCode,
  onClick,
}: SavingsOfferProps) => {
  const getTranslatedText = useLocale();
  const locale = determineLocale();

  const mapServiceTypeToBackendConst = (serviceType: string): CouponType => {
    switch (serviceType) {
      case TOW:
        return "TOW";
      case JUMP_START:
        return "JUMP_START";
      case TIRE_CHANGE:
        return "TIRE_CHANGE";
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!couponPrecursor) return;
    switch (deviceType) {
      case "Android":
        generateCouponForAndroid();
        break;
      case "iOS":
        generateCouponForiOS();
        break;
      default:
        return;
    }
  }, [couponPrecursor, deviceType]);

  const createCouponRequestPayload = (deviceType: DeviceType) => {
    onClick({
      callId,
      couponType: mapServiceTypeToBackendConst(couponType),
      deviceType,
    });
  };

  const decodeBase64ToBlob = (base64String: string): Blob => {
    try {
      const binaryString = atob(base64String);
      const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
      const blob = new Blob([bytes], { type: "application/vnd.apple.pkpass" });

      if (blob.size === 0) {
        throw new Error("Decoded blob is empty");
      }

      return blob;
    } catch (err) {
      console.error("Error decoding base64 string to Blob:", err);
      throw new Error("Failed to decode base64 string to Blob");
    }
  };

  const generateCouponForAndroid = () => {
    try {
      const url = `https://pay.google.com/gp/v/save/${couponPrecursor}`;
      window.open(url, "_blank");
    } catch (err) {
      datadogRum.addError("Error generating coupon for Android", { error: err });
    }
  }

  const generateCouponForiOS = () => {
    const blob = decodeBase64ToBlob(couponPrecursor);
    const fileName = `PEPCoupon_${callId}.pkpass`;


    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleiOSWalletClick = () => {
    createCouponRequestPayload("iOS");
  };

  const handleGoogleWalletClick = () => {
    createCouponRequestPayload("Android");
  };

  return (
    <StyledSavingsOffer>
      <Grid verticalAlign="middle">
        <Grid.Column col={20}>
          <Flex justifyContent="between">
            <Type element="h3" size="xl" weight="semibold">
              {getTranslatedText(TRANSLATION_CONSTANTS.PEP_BOYS_OFFERS)}
            </Type>
            <img
              key="rt-1"
              className="rescue-tracker-partner-logo"
              src={`/images/rt-partner-logos/${displayCode}/header.svg`}
              alt="Pep Boys"
            />
          </Flex>
          <hr />
          <Type element="p">{getTranslatedText(TRANSLATION_CONSTANTS.PEP_BOYS_COUPON)}</Type>
          <Flex>
            <Type className="cc-o-box">
              {locale === es_US ? (
                <img
                  src={GoogleWalletSpanish}
                  alt="Add to Google Wallet"
                  className="wallet-btn"
                  onClick={handleGoogleWalletClick}
                  data-testid="apple-wallet-btn-es"
                />
              ) : (
                <img
                  src={GoogleWallet}
                  alt="Add to Google Wallet"
                  className="wallet-btn"
                  onClick={handleGoogleWalletClick}
                  data-testid="apple-wallet-btn-en"
                />
              )}
            </Type>
            <Type className="cc-o-box">
              {locale === es_US ? (
                <img
                  src={GoogleWalletIOSSpanish}
                  alt="Add to Apple Wallet"
                  className="wallet-btn"
                  onClick={handleiOSWalletClick}
                  data-testid="google-wallet-btn-es"
                />
              ) : (
                <img
                  src={GoogleWalletIOS}
                  alt="Add to Apple Wallet"
                  className="wallet-btn"
                  onClick={handleiOSWalletClick}
                  data-testid="google-wallet-btn-en"
                />
              )}
            </Type>
          </Flex>
        </Grid.Column>
      </Grid>
    </StyledSavingsOffer>
  );
};
